import React, { createContext, useContext, useMemo } from 'react';

import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';

import {
  sortBooleanWithNullOrUndefinedValues,
  sortWithNullOrUndefinedValues,
} from '../internals/sort';
import { bytesToSize, withSeparators } from '../internals/utils';
import { getSortValues } from '../components/filter-table/FilterTable';
import {
  getPipesFromSystem,
  getPipesToSystem,
  getPipesWithSystemTransform,
  systemConfigSorter,
} from 'Internals/systems';

import Ellipsis from 'Common/Ellipsis';
import SystemLink from 'Common/Links/SystemLink';
import InfoTooltip from 'Common/InfoTooltip/InfoTooltip';
import ConfigStateRenderer from 'Common/table-list-renderers/config-state';
import State from 'Common/renderers/State/State';

import type { System } from '../types/system.types';
import type { Pipes } from '../types/pipes.types';
import type { SomeObject } from '../types/common.types';
import type { FilterCell } from '../types/pipesList.types';

export function BytesUsedCell({ cell: { value } }: FilterCell) {
  const { showBytesExact, toggleShowBytesExact } = useContext(SystemsListContext);
  if (isUndefined(value)) {
    return '-';
  }
  return (
    <span
      onClick={() => toggleShowBytesExact()}
      title="Click to switch between representation"
      style={{ cursor: 'pointer' }}
    >
      {showBytesExact ? withSeparators(value) : bytesToSize(value)}
    </span>
  );
}

export const SystemsListContext = createContext({
  showBytesExact: false,
  toggleShowBytesExact: () => {},
});

export const useSystemsListColumns = (
  pipes: Pipes,
  outbounds: SomeObject,
  inbounds: SomeObject,
  transforms: SomeObject
) => {
  const columns = useMemo(() => {
    return [
      {
        Header: 'System',
        fixed: true,
        id: 'systems-link',
        width: 300,
        accessor: (s: System) => ({
          id: s._id,
          link: s.link,
          description: get(s, ['config', 'original', 'description']),
        }),
        Cell: ({ cell: { value } }: FilterCell) => (
          <div style={{ display: 'flex' }}>
            <Ellipsis>
              <SystemLink systemId={value.link}>{value.id}</SystemLink>
            </Ellipsis>
            {value.description && (
              <InfoTooltip info={value.description} style={{ marginLeft: '5px' }} />
            )}
          </div>
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          return sortWithNullOrUndefinedValues(a.id, b.id);
        }),
      },
      {
        Header: 'Name',
        id: 'systems-name',
        accessor: (s: System) => ({ name: s.name, link: s.link }),
        Cell: ({ cell: { value } }: FilterCell) => (
          <SystemLink systemId={value.link}>{value.name}</SystemLink>
        ),
        sortType: getSortValues((a: SomeObject, b: SomeObject) => {
          return sortWithNullOrUndefinedValues(a.name, b.name);
        }),
      },
      {
        Header: 'Type',
        id: 'systems-type',
        accessor: 'type',
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Origin',
        id: 'systems-origin',
        accessor: 'runtime.origin',
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Pipes in',
        id: 'systems-inbound',
        accessor: (s: System) => getPipesToSystem(s._id, pipes, outbounds).length,
        align: 'right',
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Pipes out',
        id: 'systems-outbound',
        accessor: (s: System) => getPipesFromSystem(s._id, pipes, inbounds).length,
        align: 'right',
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Config',
        id: 'systems-config',
        accessor: (s: System) => s,
        align: 'center',
        Cell: ({ cell: { value } }: FilterCell) => (
          <ConfigStateRenderer value={value} type="system" />
        ),
        sortType: getSortValues(systemConfigSorter),
      },
      {
        Header: 'Config group',
        id: 'systems-config-group',
        accessor: 'configGroup',
        Cell: ({ cell: { value } }: FilterCell) => {
          return <span>{value}</span>;
        },
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'System group',
        id: 'systems-system-group',
        accessor: 'systemGroup',
        Cell: ({ cell: { value } }: FilterCell) => {
          return <span>{value}</span>;
        },
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Bytes used',
        id: 'systems-storage',
        accessor: 'bytesUsed',
        align: 'center',
        Cell: BytesUsedCell,
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Transforms',
        id: 'systems-transforms',
        accessor: (s: System) => getPipesWithSystemTransform(s._id, pipes, transforms).length,
        align: 'right',
        sortType: getSortValues(sortWithNullOrUndefinedValues),
      },
      {
        Header: 'Tenant',
        id: 'tenant-pipe',
        accessor: 'tenant',
        align: 'center',
        width: 75,
        Cell: ({ cell: { value } }: FilterCell) =>
          value !== 'Not Tenant' ? (
            <State title="Tenant pipe" className="status-green" text={value} />
          ) : (
            <State title="Not Tenant pipe" className="status-red" text="Not Tenant" />
          ),
        sortType: getSortValues((a: string, b: string) => {
          const aBool = a !== 'Not Tenant';
          const bBool = b !== 'Not Tenant';
          return sortBooleanWithNullOrUndefinedValues(aBool, bBool);
        }),
      },
    ];
  }, []);

  return columns;
};
